import { BaseReq, FailedResp } from '@paradigm/api';
import { AsyncResp } from '@paradigm/api/src/fetch-api';

import { unformatDecimal } from '@paradigm/utils/formatDecimal';

import { CreateQuoteReport, submitQuote } from '#/unified-rfqs/ui/submit-quote';
import {
  GrfqNewQuote,
  PricingReq,
} from '#/unified-rfqs/repositories/grfq-types';
import { getLegPricing } from '#/unified-rfqs/repositories/grfq-api';

import {
  LegPriceView,
  LegsPricesView,
} from '#/unified-rfqs/ui/grfq/quote-builder/views/legsPrices';
import { GrfqService } from '#/unified-rfqs/services/grfq';
import type { PricingAlgoVersion, Side } from '#/unified-rfqs/entities/shared';

interface CreateQuoteLegReq {
  readonly ratio: string;
  readonly instrument: string;
  readonly side: Side;
  readonly price?: string | null;
  readonly isHedge: boolean;
}

export interface CreatePricedQuotesArgs extends BaseReq {
  readonly rfqId: number;
  readonly venue: string;
  readonly account: string;
  readonly price: string;
  readonly quantity: string;
  readonly legPricingEnabled: boolean;
  readonly legs: readonly CreateQuoteLegReq[];
  readonly legsPrices: LegsPricesView;
  readonly isPostOnly: boolean;
  readonly side: Side;
  readonly algorithmVersion: PricingAlgoVersion;
}

export const createOrReplacePricedQuotes =
  (createQuote: typeof GrfqService.prototype.createQuote) =>
  async (args: CreatePricedQuotesArgs): AsyncResp<CreateQuoteReport> => {
    const quotesPrices = await calculateQuotesPrices(args);

    if (!quotesPrices.ok) {
      if (quotesPrices.status === 400) {
        const msg = quotesPrices.message.trim().replace(/\.+$/u, '');
        return failure({
          error: 'LegPricingError',
          message: `${msg}. Please enter individual Leg Prices.`,
        });
      }

      return quotesPrices;
    }

    const quote = prepareNewQuote(args, quotesPrices.data);

    if (quote === null) {
      return failure({
        error: 'SubmitQuoteError',
        message: 'No quote to be submitted',
      });
    }

    return submitQuote(createQuote)({ ...args, quote });
  };

interface NewQuotesPricesResp {
  readonly ok: true;
  readonly data: NewQuotesPrices;
}

async function calculateQuotesPrices(
  args: CreatePricedQuotesArgs,
): Promise<NewQuotesPricesResp | FailedResp> {
  if (args.legPricingEnabled) return getCustomStrategyQuotesPrices(args);

  if (isSingleRegularLegStrategy(args)) {
    return {
      ok: true,
      data: { prices: [args.price] },
    };
  }

  return fetchQuotesPrices(args);
}

function getCustomStrategyQuotesPrices({
  legs,
  legsPrices,
  side,
}: CreatePricedQuotesArgs): NewQuotesPricesResp {
  const prices = legsPrices.legs.map(createLegPriceBySideSelector(legs, side));
  return { ok: true, data: { prices } };
}

function isSingleRegularLegStrategy(args: CreatePricedQuotesArgs) {
  const regularLegs = args.legs.filter((leg) => !leg.isHedge);
  return regularLegs.length === 1;
}

async function fetchQuotesPrices(
  args: CreatePricedQuotesArgs,
): Promise<NewQuotesPricesResp | FailedResp> {
  const legsPricingResp = await getLegPricing(prepareLegsPricingReq(args));
  if (!legsPricingResp.ok) return legsPricingResp;

  const sidedPricesKey = args.side === 'BUY' ? 'bid_prices' : 'ask_prices';

  const { [sidedPricesKey]: prices } = legsPricingResp.data;
  return { ok: true, data: { prices } };
}

interface PrepareLegsPricingArgs extends BaseReq {
  readonly venue: string;
  readonly side: Side;
  readonly price: string;
  readonly legs: readonly CreateQuoteLegReq[];
  readonly algorithmVersion: PricingAlgoVersion;
}

function prepareLegsPricingReq({
  venue,
  price,
  side,
  legs,
  signal,
  algorithmVersion,
}: PrepareLegsPricingArgs): PricingReq {
  const sidedPriceKey = side === 'BUY' ? 'bid_price' : 'ask_price';

  return {
    venue,
    [sidedPriceKey]: price.length > 0 ? unformatDecimal(price) : undefined,
    legs: legs
      .filter((leg) => !leg.isHedge)
      .map(({ instrument, ratio, side: legSide }) => ({
        instrument,
        ratio,
        side: legSide,
      })),
    signal,
    algorithm_version: algorithmVersion,
  };
}

interface NewQuotesPrices {
  prices: readonly string[];
}

function prepareNewQuote(
  createQuoteArgs: CreatePricedQuotesArgs,
  { prices }: NewQuotesPrices,
): GrfqNewQuote | null {
  const { quantity, legs, account, isPostOnly, side } = createQuoteArgs;

  if (!isFiniteNonZero(quantity)) return null;

  return prepareQuote({
    account,
    quantity,
    legPrices: prices,
    legs,
    side,
    isPostOnly,
  });
}

export interface PrepareQuoteArgs {
  readonly account: string;
  readonly quantity: string;
  readonly legPrices: readonly string[];
  readonly legs: readonly CreateQuoteLegReq[];
  readonly side: 'BUY' | 'SELL';
  readonly isPostOnly: boolean;
}

function prepareQuote({
  account,
  quantity,
  legPrices,
  legs,
  side,
  isPostOnly,
}: PrepareQuoteArgs): GrfqNewQuote {
  const regularLegs = legs.filter((leg) => !leg.isHedge);
  const pricedRegularLegs = regularLegs.map((leg, index) => ({
    instrument: leg.instrument,
    price: unformatDecimal(legPrices[index]!),
  }));

  const hedgeLegs = legs.filter((leg) => leg.isHedge);
  const pricedHedgeLegs = hedgeLegs.map((leg) => ({
    instrument: leg.instrument,
    price: unformatDecimal(leg.price!),
  }));

  return {
    account,
    legs: [...pricedRegularLegs, ...pricedHedgeLegs],
    quantity: unformatDecimal(quantity),
    side,
    post_only: isPostOnly,
  };
}

function isFiniteNonZero(value: string) {
  const num = Number.parseFloat(unformatDecimal(value));
  return Number.isFinite(num) && num !== 0;
}

function createLegPriceBySideSelector(
  legs: readonly CreateQuoteLegReq[],
  side: 'BUY' | 'SELL',
) {
  return ({ bid, ask }: LegPriceView, index: number) => {
    const leg = legs[index]!;
    switch (side) {
      case 'BUY':
        return leg.side === 'BUY' ? bid : ask;
      case 'SELL':
        return leg.side === 'BUY' ? ask : bid;
      // no default
    }
  };
}

function failure({
  status = 400,
  error,
  message,
  data = {},
}: Partial<FailedResp> & Pick<FailedResp, 'error' | 'message'>): FailedResp {
  return {
    ok: false,
    status,
    error,
    message,
    data,
  };
}
